import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { getCurrentUser, fetchAuthSession, signOut } from 'aws-amplify/auth';
import { UserRole } from '../types/models';

export interface CurrentUser {
  userId: string;   // Cognito sub
  email: string;
  name: string;
  roles: UserRole[];
  defaultLocations: string[];
}

interface AuthContextType {
  currentUser: CurrentUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function parseRoles(raw: unknown): UserRole[] {
  if (!raw || typeof raw !== 'string') return ['Yogi'];
  return raw.split(',').map(r => r.trim()).filter(Boolean) as UserRole[];
}

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshUser = async () => {
    try {
      setIsLoading(true);
      await getCurrentUser(); // throws if not signed in
      const session = await fetchAuthSession({ forceRefresh: true });
      const payload = session.tokens?.idToken?.payload;
      if (!payload) { setCurrentUser(null); return; }

      const locationsRaw = payload['custom:locations'] as string | undefined;
      setCurrentUser({
        userId: payload['sub'] as string,
        email: (payload['email'] as string) ?? '',
        name: (payload['name'] as string) ?? (payload['cognito:username'] as string) ?? '',
        roles: parseRoles(payload['custom:role']),
        defaultLocations: locationsRaw ? locationsRaw.split(',').map(s => s.trim()).filter(Boolean) : [],
      });
    } catch {
      setCurrentUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      setCurrentUser(null);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  useEffect(() => { refreshUser(); }, []);

  return (
    <AuthContext.Provider value={{
      currentUser,
      isLoading,
      isAuthenticated: !!currentUser,
      signOut: handleSignOut,
      refreshUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
